// 音では区別できない表記の差を吸収し、候補を突き合わせるための鍵を作る
// (spec 2026-07-26 D25-2)。外部 PoC (name-picker) の kana2julius.py にある
// canon / match_key / describe を、カタカナ前提に移植したもの。
//
// **表示には使わない。突き合わせ専用。** 長音は寄せ先を決められないため
// (「オウ」に統一すると大野がオウノに、「オオ」に統一すると河野がコオノになる)、
// 鍵の上でだけ畳み、画面と TTS には元の表記をそのまま出す。
//
// 入力はカタカナを前提とする (formatName の出力、D23)。

// 音が同じで表記だけが違うカナ。どちらで書くかは音声からは決して決まらない。
// **ヅ/ヂ は両方とも実際に使われる**ので、鍵の上では寄せたうえで
// describeSpelling で差を説明し、本人に選んでもらう (D25-4)。
const SPELLING: Record<string, string> = { ヅ: "ズ", ヂ: "ジ" };

// 旧仮名。音は同じで、現代の人名表記では使われない。**寄せるだけで選択肢に出さない。**
const ARCHAIC: Record<string, string> = { ヰ: "イ", ヱ: "エ", ヲ: "オ" };

// 小書き。読みとしては大書きと同じ音 (オリィ = オリイ)。
// **拗音の ャ/ュ/ョ は含めない** — 含めるとキャノがキヤノに化ける。
// 外来音のファ/ヴィ等はここでフア/ヴイに寄るが、突き合わせは両辺に同じ変換を
// 当てるので比較結果は変わらない (表示に使わないので実害が無い)。
const SMALL: Record<string, string> = { ァ: "ア", ィ: "イ", ゥ: "ウ", ェ: "エ", ォ: "オ" };

// 音を説明する言い方。音で区別できない表記は読み上げても伝わらないので、
// 確認するときはこの言い方で言い添える。
const DESCRIPTION: Record<string, string> = {
  ヅ: "ツに濁点",
  ズ: "スに濁点",
  ヂ: "チに濁点",
  ジ: "シに濁点",
};

// 長音符の異体字。全角・半角のダッシュ類をまとめて扱う。
const LONG_MARKS = "ーｰ―−-";

// 各カナの母音。長音符を開くときと、長音の書き分けを畳むときに使う。
const VOWEL_ROWS: ReadonlyArray<readonly [string, string]> = [
  ["ア", "アカサタナハマヤラワガザダバパャヮァ"],
  ["イ", "イキシチニヒミリギジヂビピィ"],
  ["ウ", "ウクスツヌフムユルグズヅブプュゥヴ"],
  ["エ", "エケセテネヘメレゲゼデベペェヶ"],
  ["オ", "オコソトノホモヨロヲゴゾドボポョォ"],
];
const VOWEL_OF: Record<string, string> = Object.fromEntries(
  VOWEL_ROWS.flatMap(([vowel, chars]) => [...chars].map((c) => [c, vowel]))
);

// 表記のゆれを片側に寄せた形。**尋ねる価値の無い差だけを寄せる。**
export function canonKana(s: string): string {
  const out: string[] = [];
  for (const ch of s) {
    const c = SPELLING[ch] ?? ARCHAIC[ch] ?? SMALL[ch] ?? ch;
    if (LONG_MARKS.includes(c)) {
      // 長音符は直前の母音に開く。開く先が無ければ (先頭) 落とす。
      const vowel = out.length > 0 ? VOWEL_OF[out[out.length - 1]] : undefined;
      if (vowel) out.push(vowel);
      continue;
    }
    out.push(c);
  }
  return out.join("");
}

// 候補を突き合わせるための鍵。canonKana に加えて長音の書き分けを畳む。
export function matchKey(s: string): string {
  const out: string[] = [];
  for (const c of canonKana(s)) {
    const prev = out.length > 0 ? out[out.length - 1] : undefined;
    const prevVowel = prev !== undefined ? VOWEL_OF[prev] : undefined;
    // 直前の母音の伸ばし (コオ) と、オ段の「ウ」による伸ばし (コウ) を同じ形に畳む。
    const isLengthened =
      prevVowel !== undefined &&
      ((prevVowel === c && "アイウエオ".includes(c)) || (c === "ウ" && prevVowel === "オ"));
    out.push(isLengthened ? "ー" : c);
  }
  return out.join("");
}

// 2 つの表記の違いを、口で言える形にする。説明できなければ null。
// 返すのは a 側の言い方 (先に読み上げるのが a のため)。
export function describeSpelling(a: string, b: string): string | null {
  if (a.length !== b.length) return null;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i] && DESCRIPTION[a[i]]) return DESCRIPTION[a[i]];
  }
  return null;
}
